/**
 * AllocationPieChart - Reusable pie chart for showing portfolio allocation
 * Can be used for asset class, currency, sector, or any allocation visualization
 */

import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

export interface AllocationData {
  name: string;
  value: number;
  color?: string;
}

export interface AllocationPieChartProps {
  data: AllocationData[];
  title?: string;
  showLegend?: boolean;
  showTooltip?: boolean;
  showLabels?: boolean;
  height?: number;
  innerRadius?: number;
  outerRadius?: number;
  className?: string;
  colors?: string[];
}

// Default color palette for allocations
const DEFAULT_COLORS = [
  '#002B45', // Navy
  '#F7B500', // Gold
  '#28A745', // Green
  '#DC3545', // Red
  '#6C757D', // Gray
  '#17A2B8', // Cyan
  '#FFC107', // Amber
  '#6610F2', // Purple
];

const AllocationPieChart: React.FC<AllocationPieChartProps> = ({
  data,
  title,
  showLegend = true,
  showTooltip = true,
  showLabels = false,
  height = 300,
  innerRadius = 0,
  outerRadius = 80,
  className = '',
  colors = DEFAULT_COLORS
}) => {
  // Add colors to data if not provided
  const dataWithColors = data.map((item, index) => ({
    ...item,
    color: item.color || colors[index % colors.length]
  }));

  // Custom label renderer
  const renderCustomLabel = (entry: any) => {
    if (!showLabels) return null;
    return `${entry.name}: ${entry.value.toFixed(1)}%`;
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-white p-2 shadow-lg rounded border border-gray-200">
          <p className="font-semibold text-sm">{data.name}</p>
          <p className="text-sm text-gray-600">
            {data.value.toFixed(2)}%
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom legend
  const renderLegend = (props: any) => {
    const { payload } = props;
    return (
      <ul className="flex flex-wrap justify-center gap-3 mt-4">
        {payload.map((entry: any, index: number) => (
          <li key={`item-${index}`} className="flex items-center gap-1">
            <span 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-xs text-gray-600">
              {entry.value}: {entry.payload.value.toFixed(1)}%
            </span>
          </li>
        ))}
      </ul>
    );
  };

  if (!data || data.length === 0) {
    return (
      <div className={`flex items-center justify-center h-${height} ${className}`}>
        <p className="text-gray-500">No allocation data available</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={dataWithColors}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={showLabels ? renderCustomLabel : false}
            outerRadius={outerRadius}
            innerRadius={innerRadius}
            fill="#8884d8"
            dataKey="value"
          >
            {dataWithColors.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          {showTooltip && <Tooltip content={<CustomTooltip />} />}
          {showLegend && <Legend content={renderLegend} />}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AllocationPieChart;